/**
 * Shared Luhmann-ID utilities for the Luhm app.
 *
 * Luhmann IDs alternate between digit segments and letter segments:
 *   Root: 1, 2, 10 (digits only)
 *   Children of "1" (ends digit -> next: letter): 1a, 1b, 1c
 *   Children of "1a" (ends letter -> next: digits): 1a1, 1a2, 1a12
 *   And so on: 1a1a, 1a1a1, ...
 *
 * IMPORTANT: "10" is NOT a child of "1" -- it is a separate root zettel.
 */

/**
 * Get the parent ID for a given Luhmann ID.
 * Root zettels (pure digits like "1", "10") have no parent -> returns null.
 *
 * @param {string} id - Luhmann ID
 * @returns {string|null} parent ID or null for root zettels
 */
export function getParentId(id) {
  if (/^\d+$/.test(id)) return null; // root node
  // Remove last segment (single letter or multi-digit number)
  const m = id.match(/^(.*?)([a-z]|\d+)$/);
  return (m && m[1]) ? m[1] : null;
}

/**
 * Get direct children of a parent from a list of zettels.
 * Enforces the alternating digit/letter rule.
 *
 * @param {Array<{id: string}>} allZettel - array of zettel objects with at least an `id` field
 * @param {string|null} parentId - parent ID, or null/undefined for root-level zettels
 * @returns {Array<{id: string}>} direct children (unsorted)
 */
export function getDirectChildren(allZettel, parentId) {
  if (!parentId) return allZettel.filter(z => /^\d+$/.test(z.id));
  const parentEndsInDigit = /\d$/.test(parentId);
  const childPattern = parentEndsInDigit ? /^[a-z]$/ : /^\d+$/;
  return allZettel.filter(z => {
    if (z.id === parentId || !z.id.startsWith(parentId)) return false;
    return childPattern.test(z.id.slice(parentId.length));
  });
}

/**
 * Sort a list of zettel objects by their Luhmann IDs.
 * Splits IDs into alternating digit/letter segments and compares numerically/lexically.
 *
 * @param {Array<{id: string}>} list - array of zettel objects
 * @returns {Array<{id: string}>} new sorted array (does not mutate input)
 */
export function sortByLuhmann(list) {
  return [...list].sort(luhmannCompare);
}

/**
 * Comparator function for sorting zettel objects by Luhmann ID.
 *
 * @param {{id: string}} a
 * @param {{id: string}} b
 * @returns {number} negative if a < b, positive if a > b, 0 if equal
 */
export function luhmannCompare(a, b) {
  const pA = a.id.match(/(\d+|[a-z]+)/g) || [];
  const pB = b.id.match(/(\d+|[a-z]+)/g) || [];
  for (let i = 0; i < Math.max(pA.length, pB.length); i++) {
    if (i >= pA.length) return -1;
    if (i >= pB.length) return 1;
    const nA = parseInt(pA[i]), nB = parseInt(pB[i]);
    if (!isNaN(nA) && !isNaN(nB)) { if (nA !== nB) return nA - nB; }
    else { if (pA[i] !== pB[i]) return pA[i].localeCompare(pB[i]); }
  }
  return 0;
}

/**
 * Check whether childId is a true Luhmann descendant of parentId.
 * Pure startsWith is wrong: "10".startsWith("1") is true, but 10 is NOT a child of 1.
 * We check that the first character after the prefix alternates correctly.
 *
 * @param {string} childId
 * @param {string} parentId
 * @returns {boolean}
 */
export function isDescendantOf(childId, parentId) {
  if (!childId.startsWith(parentId) || childId.length <= parentId.length) return false;
  const suffix = childId[parentId.length];
  const parentEndsInDigit = /\d$/.test(parentId);
  return parentEndsInDigit ? /[a-z]/i.test(suffix) : /\d/.test(suffix);
}

/**
 * Compute next child ID client-side (mirror of server logic).
 * Fills gaps: if 1a and 1c exist, returns 1b (not 1d).
 *
 * @param {string} parentId
 * @param {Array<{id: string}>} allZettel
 * @returns {string|null} next available child ID, or null if all slots full
 */
export function getNextChildId(parentId, allZettel) {
  const parentEndsInDigit = /\d$/.test(parentId);
  const childPattern = parentEndsInDigit ? /^[a-z]$/ : /^\d+$/;
  const usedSuffixes = new Set();
  for (const z of allZettel) {
    if (z.id === parentId || !z.id.startsWith(parentId)) continue;
    const suffix = z.id.slice(parentId.length);
    if (childPattern.test(suffix)) usedSuffixes.add(suffix);
  }

  if (parentEndsInDigit) {
    for (let c = 97; c <= 122; c++) {
      const letter = String.fromCharCode(c);
      if (!usedSuffixes.has(letter)) return parentId + letter;
    }
    return null;
  } else {
    for (let n = 1; n <= 9; n++) {
      if (!usedSuffixes.has(n.toString())) return parentId + n.toString();
    }
    return null;
  }
}

/**
 * Get the next available top-level (root) ID.
 * Fills gaps: if 1 and 3 exist, returns 2 (not 4).
 *
 * @param {Array<{id: string}>} allZettel
 * @returns {string} next root ID
 */
export function getNextTopLevelId(allZettel) {
  const topLevel = new Set(allZettel.filter(z => /^\d+$/.test(z.id)).map(z => parseInt(z.id)));
  for (let n = 1; n <= 9999; n++) {
    if (!topLevel.has(n)) return n.toString();
  }
  return (topLevel.size + 1).toString();
}
